import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';

function phoneRequired(control: AbstractControl): ValidationErrors | null {
    const value = control.value as { contactNumber?: string } | null;
    return value?.contactNumber?.trim() ? null : { required: true };
}
import { Router, RouterModule } from '@angular/router';
import { forkJoin, catchError, of, Subscription } from 'rxjs';
import { SUBSCRIPTION_STATUS } from '../@core/models/enums';
import { IOrganization, PAYMENT_MODE, PlanFeature } from '../@core/models/model';
import { PlanService } from '../@core/services/plan.service';
import { SubscriptionStateService } from '../@core/services/subscription-state.service';
import { ProspectService, WEBSITE_TYPE } from '../@core/services/prospect.service';
import { PhoneInputComponent, PhoneNumberValue } from '../@shared/components/phone-input/phone-input.component';
import { OrganizationSetupFormService } from '../contract-management/services/organization-setup-from.service';
import { PaymentService } from '../contract-management/services/payment.service';
import { SVG } from '../common/svgs';
import { SanitizeHtmlPipe } from "../@shared/pipes/sanitizeHtml.pipe";
import { TextCasePipe } from "../@shared/pipes/textCase.pipe";
import { EntityUnit, IPlan, TermUnit, TERM_UNIT_LABELS } from './models';
import { AssetUrlPipe } from '../@shared/pipes/asset-url.pipe';
import { ButtonComponent } from '../@core/components/ui/button/button.component';
import { IconComponent } from '../@core/components/ui/icon/icon.component';
import { CarouselComponent } from '../@core/components/ui/carousel';
import { SelectButtonComponent } from '../@core/components/ui/select-button/select-button.component';
import { SegmentedOption } from '../@core/components/ui/segmented/segmented.component';
import { CaseType } from '../@shared/services/case-format.service';
import { CheckoutDialogComponent, CheckoutConfig, CheckoutSource } from './checkout-dialog';
import { isDowngradePlan, termToMonthlyDivisor } from './plan-pricing.utils';
import { DialogComponent } from '../@core/components/ui/dialog/dialog.component';
import { ToastService } from '../@core/services/toast.service';
import { PhoneNumberDialogComponent } from '../@shared/components/phone-number-dialog/phone-number-dialog.component';
import { AuthenticationService } from '../@core/services/authentication.service';
import { IUser } from '../@core/models/model';
import { hasBillingAccess } from '../@core/utils/permissions.utils';

@Component({
    selector: 'app-pricing',
    standalone: true,
    imports: [RouterModule, FormsModule, ReactiveFormsModule, CommonModule, DatePipe, PhoneInputComponent, SanitizeHtmlPipe, TextCasePipe, ButtonComponent, IconComponent, CarouselComponent, SelectButtonComponent, CheckoutDialogComponent, AssetUrlPipe, PhoneNumberDialogComponent, DialogComponent],
    templateUrl: './pricing.component.html',
    styleUrl: './pricing.component.scss'
})
export class PricingComponent implements OnInit, OnDestroy {
    // UI State

    SVG = SVG;
    PAYMENT_MODE = PAYMENT_MODE;

    ui = {
        showContactSalesModal: false,
        showSuccessDialog: false,
        showCheckoutDialog: false,
        showCancelDialog: false,
        isYearlySelected: false,
        selectedPlan: null as IPlan | null,
        isLoadingCheckout: false,
        isCancellingSubscription: false,
        isLoadingPortal: false,
        isLoading: true,
        isLoadingDowngradeSchedule: false,
        isPendingDowngrade: false
    };

    currentUser: IUser | null = null;
    cancelAtDate: Date | null = null;

    PLAN_FEATURE = PlanFeature;

    // Form
    contactForm: FormGroup;
    phoneNumberValue: PhoneNumberValue = { countryCode: '44', contactNumber: '', countryShortName: 'GB' };
    organizationData!: IOrganization;

    plans: (IPlan & {
        currentView: TermUnit,
        priceViewOptions: SegmentedOption[],
        selectedEntity: keyof typeof EntityUnit,
        isOrganizationEntity: boolean
    })[] = [];
    processedPlans: any[] = [];

    CASE_TYPE = CaseType;
    TERM_UNIT_LABELS = TERM_UNIT_LABELS;
    EntityUnit = EntityUnit;
    CheckoutSource = CheckoutSource;

    // Top-level entity filter
    selectedEntityType: EntityUnit = EntityUnit.ORGANIZATION;

    showPhoneNumberDialog = false;

    private subscriptionUpdatedSub?: Subscription;

    // Filtered plans based on selected entity type
    // If only one entity type exists, filter by that; otherwise use selectedEntityType
    // Also includes plans with empty pricing arrays (show for all entity types)
    get filteredPlans() {
        const entityToFilter = this.singleAvailableEntityType || this.selectedEntityType;
        return this.plans.filter(plan => {
            // Include plans with empty pricing array
            if (!plan.pricing || plan.pricing.length === 0) {
                return true;
            }
            // Include plans that have pricing for the selected entity type
            return plan.pricing.some((p: any) => p.entity === entityToFilter);
        });
    }

    constructor(
        private fb: FormBuilder,
        private organizationService: OrganizationSetupFormService,
        private paymentService: PaymentService,
        private planService: PlanService,
        private prospectService: ProspectService,
        private router: Router,
        public subscriptionState: SubscriptionStateService,
        private toastService: ToastService,
        private authService: AuthenticationService
    ) {
        this.contactForm = this.fb.group({
            fullName: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            phoneNumber: [this.phoneNumberValue, phoneRequired],
            company: ['', Validators.required],
            message: ['', Validators.required]
        });
        this.authService.currentUser.subscribe(user => {
            this.currentUser = user ?? null;
        });
    }

    ngOnInit() {
        this.loadAllData();
        this.subscriptionUpdatedSub = this.subscriptionState.socketRefresh$.subscribe(() => {
            this.refreshOrgSubscription();
        });
    }

    ngOnDestroy() {
        this.subscriptionUpdatedSub?.unsubscribe();
    }

    private loadAllData() {

        const plans$ = this.planService.getPlans().pipe(
            catchError(error => {
                console.error('Error fetching plan features:', error);
                return of({ data: [] });
            })
        );

        const organization$ = this.organizationService.fetchOrganizationSetupData().pipe(
            catchError(error => {
                console.error('Error fetching organization details:', error);
                return of({ success: false, data: null });
            })
        );

        // Check if subscription data is already loaded, if not, fetch it
        const subscription$ = this.subscriptionState.subscriptionDetails()
            ? of({ success: true, data: this.subscriptionState.subscriptionDetails() })
            : this.subscriptionState.fetchSubscriptionDetails().pipe(
                catchError(error => {
                    console.error('Error fetching subscription details:', error);
                    return of({ success: false, data: null });
                })
            );

        const orgSubscription$ = this.subscriptionState.fetchOrgSubscriptionDetails().pipe(
            catchError(error => {
                console.error('Error fetching org subscription details:', error);
                return of({ success: false, data: null });
            })
        );

        // Use forkJoin to wait for all observables to complete
        forkJoin({
            plans: plans$,
            organization: organization$,
            subscription: subscription$,
            orgSubscription: orgSubscription$
        }).subscribe({
            next: (results) => {
                // Process plan features
                const plans = results.plans.data as IPlan[];


                // Determine which entity to use: single available entity or default selectedEntityType
                // First, check what entities are available across all plans
                const allEntities = new Set<EntityUnit>();
                plans.forEach(plan => {
                    if (plan.pricing) {
                        plan.pricing.forEach((p: any) => {
                            allEntities.add(p.entity);
                        });
                    }
                });

                // If only one entity type exists, use it; otherwise use selectedEntityType
                const entityToUse = allEntities.size === 1
                    ? Array.from(allEntities)[0]
                    : this.selectedEntityType;

                // Update selectedEntityType if only one entity exists
                if (allEntities.size === 1) {
                    this.selectedEntityType = entityToUse;
                }

                const selectedEntity = entityToUse;
                this.plans = plans.map(plan => {
                    const priceViewOptions = this.getPriceViewOptionsForEntity(plan, selectedEntity);

                    return {
                        ...plan,
                        currentView: (priceViewOptions.length > 0 ? priceViewOptions[0].value : TermUnit.MONTH) as TermUnit,
                        priceViewOptions: priceViewOptions,
                        selectedEntity: selectedEntity,
                        isOrganizationEntity: selectedEntity === EntityUnit.ORGANIZATION
                    };
                });


                // Process organization details
                if (results.organization.success) {
                    this.organizationData = results.organization.data as IOrganization;
                }

                // Apply custom plan pricing overlay if org has a sales-assigned plan
                if (results.orgSubscription.success && results.orgSubscription.data) {
                    const orgPlan = this.subscriptionState.orgPlan();
                    if (orgPlan && (orgPlan as any).linkedDefaultPlan && orgPlan.pricing?.length > 0) {
                        const linkedDefaultPlanId = (orgPlan as any).linkedDefaultPlan?.toString();
                        const matchingPlanIndex = this.plans.findIndex(
                            p => (p as any)._id?.toString() === linkedDefaultPlanId
                        );
                        if (matchingPlanIndex !== -1) {
                            const matchingPlan = this.plans[matchingPlanIndex];
                            matchingPlan.pricing = orgPlan.pricing;
                            if (orgPlan.showPriceToCustomer !== undefined) {
                                matchingPlan.showPriceToCustomer = orgPlan.showPriceToCustomer;
                            }
                            // Rebuild priceViewOptions with the org-specific pricing
                            const updatedOptions = this.getPriceViewOptionsForEntity(matchingPlan, entityToUse);
                            matchingPlan.priceViewOptions = updatedOptions;
                            if (updatedOptions.length > 0) {
                                matchingPlan.currentView = updatedOptions[0].value as TermUnit;
                            }
                        }
                    }
                }

                setTimeout(() => {
                    this.ui.isLoading = false;
                }, 500);
            },
            error: (error) => {
                console.error('Error in forkJoin:', error);
                // Still turn off loading even if there's an error
                this.ui.isLoading = false;
            }
        });
    }

    /**
     * Get current plan from subscription state
     */
    getCurrentPlan(): IPlan | null {
        return this.getOrgPlan() ?? this.subscriptionState.plan();
    }

    getOrgPlan(): any {
        return this.subscriptionState.orgPlan();
    }

    private refreshOrgSubscription(): void {
        this.subscriptionState.refreshOrgSubscriptionDetails();
    }

    orgHasPaid(): boolean { return this.subscriptionState.orgHasPaid(); }
    orgIsActive(): boolean { return this.subscriptionState.orgIsActive(); }
    orgIsPaymentFailed(): boolean { return this.subscriptionState.orgIsPaymentFailed(); }
    orgIsTrial(): boolean { return this.subscriptionState.orgIsTrial(); }
    orgIsPaymentUnpaid(): boolean { return this.subscriptionState.orgIsPaymentUnpaid(); }
    orgIsCancelPending(): boolean { return this.subscriptionState.orgIsCancelPending(); }
    orgIsDowngradePending(): boolean { return this.subscriptionState.orgIsDowngradePending(); }
    orgHasSubscriptionEnded(): boolean { return this.subscriptionState.orgHasSubscriptionEnded(); }
    orgHasActiveStripeSubscription(): boolean { return this.subscriptionState.orgHasActiveStripeSubscription(); }

    get isSalesPaymentMode(): boolean {
        return this.subscriptionState.orgSubscriptionDetails()?.paymentMode === PAYMENT_MODE.SALES;
    }

    /**
     * Check if subscription is active
     */
    isSubscriptionActive(): boolean {
        return this.orgIsActive();
    }

    isSubscriptionExpired(): boolean {
        return this.subscriptionState.orgSubscriptionDetails()?.status === SUBSCRIPTION_STATUS.INACTIVE;
    }

    /**
     * Check if a plan is the currently selected plan
     * Uses linkedDefaultPlan to match organization plans with their default plan
     */
    isCurrentPlan(plan: IPlan): boolean {
        const currentPlan = this.getOrgPlan();
        const planId = (plan as any)._id;

        if (!currentPlan) {
            return false;
        }

        const currentPlanId = typeof currentPlan === 'string' ? currentPlan : (currentPlan as any)._id;

        if (!currentPlanId || !planId) {
            return false;
        }

        // When orgSubscription.plan is a string (not populated), fall back to subscriptionState
        // to get the full plan object which contains linkedDefaultPlan
        const currentPlanObj = typeof currentPlan === 'object' ? currentPlan : this.subscriptionState.plan();

        // If current plan has linkedDefaultPlan, it means it's a custom/organization plan
        // Compare the linkedDefaultPlan with the displayed plan's ID
        if (currentPlanObj && (currentPlanObj as any).linkedDefaultPlan) {
            const linkedDefaultPlanId = (currentPlanObj as any).linkedDefaultPlan;
            return linkedDefaultPlanId?.toString() === planId?.toString();
        }

        // Otherwise, compare IDs directly (both are default plans)
        return currentPlanId?.toString() === planId?.toString();
    }

    private getMonthlyPrice(plan: IPlan): number {
        if (!plan.pricing || plan.pricing.length === 0) return 0;
        const row = plan.pricing.find((p: any) => p.entity === this.selectedEntityType);
        if (!row) return 0;
        const divisor = termToMonthlyDivisor(row.term);
        return divisor > 0 ? row.price / divisor : 0;
    }

    /**
     * Get formatted status text for display
     */
    getStatusText(): string {
        const status = this.subscriptionState.orgSubscriptionDetails()?.status || '';
        if (!status) return '';

        if (this.orgIsTrial()) return 'Trial';
        if (this.orgIsPaymentFailed()) return 'Payment Failed';
        if (this.orgIsPaymentUnpaid()) return 'Unpaid';
        if (status === SUBSCRIPTION_STATUS.INACTIVE) return 'Expired';
        return status;
    }

    onViewModeChange(plan: any, event: any) {
        // Update the plan's currentView when user changes the select button
        if (plan && event && event.value) {
            const newView = event.value as TermUnit;
            plan.currentView = newView;

            // Also update the plan in the plans array to persist the change
            const planId = (plan as any)._id;
            const planIndex = this.plans.findIndex(p => (p as any)._id === planId);
            if (planIndex !== -1) {
                this.plans[planIndex].currentView = newView;
            }
        }
    }

    onTopLevelEntityChange(entity: EntityUnit | string): void {
        const newEntity = typeof entity === 'string' ? (entity as EntityUnit) : entity;
        this.selectedEntityType = newEntity;

        // Update all plans to use the new entity
        this.plans.forEach(plan => {
            const priceViewOptions = this.getPriceViewOptionsForEntity(plan, newEntity);
            if (priceViewOptions.length > 0) {
                plan.priceViewOptions = priceViewOptions;
                plan.currentView = priceViewOptions[0].value as TermUnit;
                plan.selectedEntity = newEntity;
                plan.isOrganizationEntity = newEntity === EntityUnit.ORGANIZATION;
            }
        });
    }

    onEntityToggleChange(plan: any, isOrganization: boolean): void {

        const newEntity = isOrganization ? EntityUnit.ORGANIZATION : EntityUnit.USER;
        plan.selectedEntity = newEntity;
        plan.isOrganizationEntity = isOrganization;

        // Update priceViewOptions for this specific plan
        plan.priceViewOptions = this.getPriceViewOptionsForEntity(plan, newEntity);

        // Set currentView to first available option for the new entity
        if (plan.priceViewOptions.length > 0) {
            plan.currentView = plan.priceViewOptions[0].value;
        }
    }

    getPriceViewOptionsForEntity(plan: IPlan, entity: EntityUnit): SegmentedOption[] {
        // Handle empty pricing array
        if (!plan.pricing || plan.pricing.length === 0) {
            return [];
        }
        return plan.pricing
            .filter((p: any) => p.entity === entity)
            .map(p => ({
                value: p.term,
                label: this.TERM_UNIT_LABELS[p.term]
            }));
    }

    // Check if plans have multiple entity types across all plans
    // Returns true if there are multiple entity types (need to show filter)
    // Returns false if all plans have only a single entity type (hide filter)
    get hasPlansWithMultipleEntities(): boolean {
        if (this.plans.length === 0) return false;

        const allEntities = new Set<EntityUnit>();

        // Collect all unique entity types from all plans
        this.plans.forEach(plan => {
            if (plan.pricing) {
                plan.pricing.forEach((p: any) => {
                    allEntities.add(p.entity);
                });
            }
        });

        // If more than one entity type exists across all plans, show filter
        return allEntities.size > 1;
    }

    // Get the single available entity type if only one exists across all plans
    get singleAvailableEntityType(): EntityUnit | null {
        const allEntities = new Set<EntityUnit>();

        this.plans.forEach(plan => {
            if (plan.pricing) {
                plan.pricing.forEach((p: any) => {
                    allEntities.add(p.entity);
                });
            }
        });

        // If only one entity type exists, return it
        if (allEntities.size === 1) {
            return Array.from(allEntities)[0];
        }

        return null;
    }


    togglePricing(selectedType: 'monthly' | 'yearly') {
        this.ui.isYearlySelected = selectedType === 'yearly';
    }

    openContactSalesModal() {
        this.ui.showContactSalesModal = true;
    }

    closeContactSalesModal() {
        this.ui.showContactSalesModal = false;
        // Reset form
        this.contactForm.reset();
        this.phoneNumberValue = { countryCode: '44', contactNumber: '', countryShortName: 'GB' };
        // Reset form to initial state with proper default values
        this.contactForm.patchValue({
            fullName: '',
            email: '',
            phoneNumber: this.phoneNumberValue,
            company: '',
            companyCategory: '',
            products: '',
            termsAccepted: false
        });
    }

    onPhoneNumberChange(phoneValue: PhoneNumberValue) {
        this.phoneNumberValue = phoneValue;
    }

    submitContactForm() {
        this.contactForm.markAllAsTouched();
        if (this.contactForm.valid) {
            // Format data for backend
            const formValue = this.contactForm.value;
            const formattedData = {
                fullName: formValue.fullName,
                email: formValue.email,
                phoneNumber: formValue.phoneNumber.countryCode + " " + formValue.phoneNumber.contactNumber,
                company: formValue.company,
                message: formValue.message,
                typeOfWebsite: WEBSITE_TYPE.AI,
                source: 'AI_CONTACT_US'
            };
            // Close contact modal and show success dialog
            this.ui.showContactSalesModal = false;
            this.ui.showSuccessDialog = true;

            this.prospectService.contactUs(formattedData).subscribe(res => {
                if (res.success) {
                    this.organizationService.updateContactSales(true).subscribe(res => {
                        if (res.success) {
                            this.ui.showSuccessDialog = true;
                        }
                    });
                }
            });
        }
    }

    closeSuccessDialog() {
        this.ui.showSuccessDialog = false;
    }

    goBackToHome() {
        this.ui.showSuccessDialog = false;
        this.router.navigate(['']);
    }

    openCheckoutDialog(plan: any) {
        this.ui.selectedPlan = plan;
        this.ui.showCheckoutDialog = true;
    }

    closeCheckoutDialog() {
        this.ui.showCheckoutDialog = false;
        this.ui.selectedPlan = null;
        this.ui.isPendingDowngrade = false;
    }

    proceedToCheckout(config: CheckoutConfig) {
        if (this.ui.isLoadingCheckout) {
            return; // Prevent multiple clicks
        }

        this.ui.isLoadingCheckout = true;

        if (this.ui.isPendingDowngrade && this.orgIsActive() && this.orgHasPaid() && !this.orgIsTrial()) {
            const planName = (this.ui.selectedPlan as any)?.name || '';
            this.paymentService.scheduleDowngrade(config.planId).subscribe({
                next: (res) => {
                    if (res.success) {
                        this.toastService.success(`Downgrade to ${planName} scheduled for ${new Date((res.data as any).effectiveAt).toLocaleDateString()}.`);
                        this.subscriptionState.refreshSubscriptionDetails().subscribe();
                        this.refreshOrgSubscription();
                        this.closeCheckoutDialog();
                    } else {
                        this.toastService.error((res as any).message || 'Failed to schedule downgrade.');
                    }
                    this.ui.isLoadingCheckout = false;
                },
                error: () => {
                    this.toastService.error('Failed to schedule downgrade.');
                    this.ui.isLoadingCheckout = false;
                }
            });
            return;
        }

        const checkoutData = {
            planId: config.planId,
            numberOfUsers: config.numberOfUsers,
            setupFee: config.setupFee,
            trainingFee: config.trainingFee,
            aiEducationFee: config.aiEducationFee,
            customFees: config.customFees || [],
            ...(config.proratedCharge != null && { proratedCharge: config.proratedCharge }),
            billing: {
                askBudget: config.billing.askBudget,
                agentBudget: config.billing.agentBudget,
                price: config.billing.price,
                entity: config.billing.entity,
                term: config.billing.term
            }
        };

        this.paymentService.createCheckoutSession(checkoutData).subscribe({
            next: (res) => {
                if (res.success) {
                    sessionStorage.setItem('payment_success', 'true');
                    window.open(res.data as any, '_blank');
                    this.closeCheckoutDialog();
                }
                this.ui.isLoadingCheckout = false;
            },
            error: (error) => {
                console.error('Error creating checkout session:', error);
                this.ui.isLoadingCheckout = false;
            }
        });
    }

    shouldShowPricingToggle(plan: any): boolean {
        // Only show toggle if plan has both monthly and yearly pricing and no active subscription
        return plan.pricing && plan.pricing.length > 1 && !this.isSubscriptionActive();
    }

    getCurrentPrice(plan: IPlan & { currentView: TermUnit; selectedEntity?: EntityUnit }): { price: number; entity: EntityUnit; term: TermUnit; } {
        const view = plan.currentView;
        const selectedEntity = (plan as any).selectedEntity || this.selectedEntityType;

        // If this is the current plan and the subscription plan is a custom/org plan (has linkedDefaultPlan),
        // use the actual subscription plan's pricing instead of the default plan's pricing
        if (this.isCurrentPlan(plan)) {
            const currentPlan = this.getOrgPlan() as IPlan | null;
            if (currentPlan && (currentPlan as any).linkedDefaultPlan && currentPlan.pricing?.length > 0) {
                return currentPlan.pricing.find(p => p.term === view && p.entity === selectedEntity) ||
                    currentPlan.pricing.find(p => p.entity === selectedEntity) ||
                    currentPlan.pricing[0];
            }
        }

        // Handle empty pricing array
        if (!plan.pricing || plan.pricing.length === 0) {
            return { price: 0, entity: this.selectedEntityType, term: TermUnit.MONTH };
        }

        return plan.pricing.find(p => p.term === view && p.entity === selectedEntity) ||
            plan.pricing.find(p => p.entity === selectedEntity) ||
            { price: 0, entity: EntityUnit.USER, term: TermUnit.MONTH };
    }

    hasPricingForSelectedEntity(plan: IPlan): boolean {
        // Plans with empty pricing array are considered to have pricing (they show for all entity types)
        if (!plan.pricing || plan.pricing.length === 0) {
            return true;
        }
        return plan.pricing.some((p: any) => p.entity === this.selectedEntityType);
    }

    hasMultipleTermsForSelectedEntity(plan: IPlan): boolean {
        // Plans with empty pricing array don't have multiple terms
        if (!plan.pricing || plan.pricing.length === 0) {
            return false;
        }
        const selectedEntity = this.selectedEntityType;
        const terms = plan.pricing
            .filter((p: any) => p.entity === selectedEntity)
            .map(p => p.term);
        return terms.length > 1;
    }

    isLoadingPlan(plan: any): boolean {
        return this.ui.isLoadingCheckout && this.ui.selectedPlan === plan;
    }

    getStatusColorClass(plan: IPlan): { border: string; badge: string } {
        if (!this.isCurrentPlan(plan)) return { border: 'border-gray-200', badge: 'bg-green-500' };
        if (this.orgIsPaymentFailed()) return { border: 'border-red-500', badge: 'bg-red-500' };
        if (this.orgIsPaymentUnpaid()) return { border: 'border-amber-500', badge: 'bg-amber-500' };
        return { border: 'border-green-500', badge: 'bg-green-500' };
    }

    shouldShowPriceToCustomer(plan: IPlan): boolean {
        const orgPlan = this.getOrgPlan();
        if (orgPlan) {
            const linkedDefaultPlanId = (orgPlan as any).linkedDefaultPlan;
            if (linkedDefaultPlanId) {
                const planId = (plan as any)._id;
                if (linkedDefaultPlanId?.toString() === planId?.toString()) {
                    return !!(orgPlan as any).showPriceToCustomer;
                }
            }
        }
        return !!(plan as any).showPriceToCustomer;
    }

    shouldShowContactSales(plan: IPlan): boolean {
        const orgPlan = this.getOrgPlan();
        if (!orgPlan) return false;
        const linkedDefaultPlanId = (orgPlan as any).linkedDefaultPlan;
        if (!linkedDefaultPlanId) return false;
        const planId = (plan as any)._id;
        return linkedDefaultPlanId?.toString() === planId?.toString() && !!(orgPlan as any).showContactSalesToCustomer;
    }

    handlePlanAction(plan: any): void {
        if (this.shouldShowContactSales(plan) && !(this.organizationData && this.organizationData.contactedSales)) {
            this.handleContactSales(plan);
        } else if (this.shouldShowPriceToCustomer(plan)) {
            this.handleCheckout(plan);
        }
    }

    handleContactSales(plan: any): void {
        if (this.organizationData && this.organizationData.contactedSales) return;
        this.planService.sendUserDataToContactSales(plan.name).subscribe((res: any) => {
            if (res.code === 1004) {
                this.ui.selectedPlan = plan;
                this.openPhoneNumberDialog();
            } else if (res.success) {
                this.toastService.success('Request to sales sent successfully');
            }
        });
    }

    handleCheckout(plan: any): void {
        const currentPlan = this.getOrgPlan() as IPlan | null;
        const isCurrentOrgPlan = this.isCurrentPlan(plan) && currentPlan && (currentPlan as any).linkedDefaultPlan;
        this.ui.isPendingDowngrade = !!(currentPlan && isDowngradePlan(currentPlan, plan as IPlan));
        this.openCheckoutDialog(isCurrentOrgPlan ? currentPlan : plan);
    }

    cancelScheduledDowngrade(): void {
        this.paymentService.cancelScheduledDowngrade().subscribe({
            next: (res) => {
                if (res.success) {
                    this.toastService.success('Scheduled downgrade cancelled.');
                    this.subscriptionState.refreshSubscriptionDetails().subscribe();
                    this.refreshOrgSubscription();
                } else {
                    this.toastService.error((res as any).message || 'Failed to cancel downgrade.');
                }
            },
            error: () => this.toastService.error('Failed to cancel downgrade.')
        });
    }



    openPhoneNumberDialog() {
        this.showPhoneNumberDialog = true;
    }

    onPhoneNumberSubmitted() {
        // Retry the sales contact request after phone number is updated
        this.handlePlanAction(this.ui.selectedPlan);
    }

    get canCancelSubscription(): boolean {
        return !!(
            hasBillingAccess(this.currentUser) &&
            this.orgHasActiveStripeSubscription() &&
            !this.orgIsCancelPending()
        );
    }

    get billingCycleEndDate(): Date | null {
        const date = this.subscriptionState.orgSubscriptionDetails()?.cancelAtPeriodEndDate;
        return date ? new Date(date) : null;
    }

    get pendingDowngradeDate(): Date | null {
        const d = this.subscriptionState.orgSubscriptionDetails()?.pendingDowngrade?.effectiveAt;
        return d ? new Date(d) : null;
    }

    get pendingDowngradePlanName(): string | null {
        return this.subscriptionState.orgSubscriptionDetails()?.pendingDowngrade?.targetPlan?.name ?? null;
    }

    openCustomerPortal(): void {
        if (this.ui.isLoadingPortal) return;
        this.ui.isLoadingPortal = true;
        this.paymentService.createCustomerPortalSession().subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    window.open(res.data, '_blank');
                } else {
                    this.toastService.error('Failed to open billing portal. Please try again.');
                }
                this.ui.isLoadingPortal = false;
            },
            error: () => {
                this.toastService.error('Failed to open billing portal. Please try again.');
                this.ui.isLoadingPortal = false;
            }
        });
    }

    openCancelDialog(): void {
        this.ui.showCancelDialog = true;
    }

    closeCancelDialog(): void {
        this.ui.showCancelDialog = false;
    }

    revokeSubscriptionCancellation(): void {
        this.paymentService.revokeSubscriptionCancellation().subscribe({
            next: (res) => {
                if (res.success) {
                    this.toastService.success('Subscription cancellation revoked successfully.');
                    this.subscriptionState.refreshSubscriptionDetails().subscribe();
                    this.refreshOrgSubscription();
                } else {
                    this.toastService.error(res.message || 'Failed to revoke cancellation.');
                }
            },
            error: () => {
                this.toastService.error('Failed to revoke cancellation. Please try again.');
            }
        });
    }

    confirmCancelSubscription(): void {
        if (this.ui.isCancellingSubscription) return;
        this.ui.isCancellingSubscription = true;
        this.paymentService.cancelSubscription().subscribe({
            next: (res) => {
                if (res.success) {
                    this.cancelAtDate = res.data?.cancelAtDate ? new Date(res.data.cancelAtDate) : null;
                    this.toastService.success('Subscription cancelled. You will retain access until the end of your billing cycle.');
                    this.subscriptionState.refreshSubscriptionDetails().subscribe();
                    this.refreshOrgSubscription();
                } else {
                    this.toastService.error(res.message || 'Failed to cancel subscription.');
                }
                this.ui.isCancellingSubscription = false;
                this.ui.showCancelDialog = false;
            },
            error: () => {
                this.toastService.error('Failed to cancel subscription. Please try again.');
                this.ui.isCancellingSubscription = false;
                this.ui.showCancelDialog = false;
            }
        });
    }

    // Track by function for carousel performance
    trackByPlanId(index: number, plan: any): any {
        // Use _id if available, otherwise fallback to index
        return (plan as any)?._id || plan?.id || `plan-${index}`;
    }

    /**
     * Get plan context - returns the actual plan object from plans array
     * This ensures that changes to currentView are preserved
     */
    getPlanContext(plan: IPlan & {
        currentView?: TermUnit,
        priceViewOptions?: SegmentedOption[],
        selectedEntity?: EntityUnit,
        isOrganizationEntity?: boolean
    }): IPlan & {
        currentView: TermUnit,
        priceViewOptions: SegmentedOption[],
        selectedEntity: EntityUnit,
        isOrganizationEntity: boolean
    } {
        // Find the actual plan object from plans array - this preserves changes to currentView
        const planId = (plan as any)._id;
        const actualPlan = this.plans.find(p => (p as any)._id === planId);

        // If plan found in array, return it directly (it already has all required properties)
        if (actualPlan) {
            // For current plan, initialize from subscription if currentView hasn't been set by user
            if (this.isCurrentPlan(plan) && !actualPlan.currentView) {
                const orgSub = this.subscriptionState.orgSubscriptionDetails();
                if (orgSub) {
                    const subscription = orgSub as any;

                    // Use billing.term if available, otherwise fallback to billingCycle
                    // Convert selectedEntity to EntityUnit if needed
                    const planEntity: EntityUnit = (typeof actualPlan.selectedEntity === 'string'
                        ? EntityUnit[actualPlan.selectedEntity as keyof typeof EntityUnit]
                        : actualPlan.selectedEntity) || this.selectedEntityType;

                    // Get term from billing.term if available
                    const billingTerm = (subscription as any).billing?.term;

                    if (billingTerm) {
                        // Use billing.term directly
                        const termOption = plan.pricing.find((p: any) =>
                            p.term === billingTerm && p.entity === planEntity
                        ) || plan.pricing.find((p: any) => p.term === billingTerm);

                        if (termOption) {
                            actualPlan.currentView = billingTerm as TermUnit;
                        }
                    } else {
                        // Fallback to billingCycle for backward compatibility
                        if ((subscription as any).billingCycle === 'monthly') {
                            const monthOption = plan.pricing.find((p: any) =>
                                p.term === TermUnit.MONTH && p.entity === planEntity
                            ) || plan.pricing.find((p: any) => p.term === TermUnit.MONTH);

                            if (monthOption) {
                                actualPlan.currentView = TermUnit.MONTH;
                            }
                        } else if ((subscription as any).billingCycle === 'yearly') {
                            const yearOption = plan.pricing.find((p: any) =>
                                p.term === TermUnit.YEAR && p.entity === planEntity
                            ) || plan.pricing.find((p: any) => p.term === TermUnit.YEAR);

                            if (yearOption) {
                                actualPlan.currentView = TermUnit.YEAR;
                            }
                        }
                    }
                }
            }

            return actualPlan as IPlan & {
                currentView: TermUnit,
                priceViewOptions: SegmentedOption[],
                selectedEntity: EntityUnit,
                isOrganizationEntity: boolean
            };
        }

        // Fallback: return plan with defaults (shouldn't happen in normal flow)
        const selectedEntity: EntityUnit = (typeof plan.selectedEntity === 'string'
            ? EntityUnit[plan.selectedEntity as keyof typeof EntityUnit]
            : plan.selectedEntity) || this.selectedEntityType;
        const priceViewOptions = plan.priceViewOptions || this.getPriceViewOptionsForEntity(plan, selectedEntity);
        const currentView = plan.currentView || (priceViewOptions.length > 0 ? priceViewOptions[0].value : TermUnit.MONTH) as TermUnit;

        return {
            ...plan,
            currentView: currentView,
            priceViewOptions: priceViewOptions,
            selectedEntity: selectedEntity,
            isOrganizationEntity: selectedEntity === EntityUnit.ORGANIZATION
        };
    }
}
